import { makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableFooter from "@material-ui/core/TableFooter";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Tooltip from "@material-ui/core/Tooltip";
import PrintIcon from "@material-ui/icons/PrintOutlined";
import { CSSProperties } from "@material-ui/styles";
import Skeleton from "@saleor/components/Skeleton";
import TableCellHeader from "@saleor/components/TableCellHeader";
import TablePagination from "@saleor/components/TablePagination";
import { maybe, renderCollection } from "@saleor/misc";
import { OrderListUrlSortField } from "@saleor/motherOrders/urls";
import { onTimeIsTrue } from "@saleor/motherOrders/utils/handlers";
import RemittanceManualAdjustmentFlag from "@saleor/remittanceRunner/components/RemittanceManualAdjustmentFlag";
import { ListProps, SortPage } from "@saleor/types";
import {
  DeliveryTypeEnum as DelEnum,
  DeliveryTypeEnum,
  OrderVerticalType,
  SmopOrderStatusEnum
} from "@saleor/types/globalTypes";
import { getPaymentChannelCode } from "@saleor/utils/paymentChannelCode/paymentChannelCode";
import { getArrowDirection } from "@saleor/utils/sort";
import classNames from "classnames";
import moment from "moment-timezone";
import React from "react";
import { FormattedMessage } from "react-intl";

import { MotherOrderList_orders_edges_node } from "../../types/MotherOrderList";
import OrderDeliveryType from "../OrderDeliveryType";
import OrderStatus from "../OrderStatus/OrderStatus";
import AgingCell from "./components/AgingCell";
import BrandCell from "./components/BrandCell";
import CancelReasonField from "./components/CancelReasonField";
import CourierName from "./components/CourierName";
import CustomerCell from "./components/CustomerCell";
import DropdownStatus from "./components/DropdownStatus";
import OpsAdminReasonForLate from "./components/OpsAdminReasonForLateField";
import RemarksCell from "./components/RemarksCell";
import RiderCell from "./components/RiderCell";
import ThreePLStatusCell from "./components/ThreePLStatusCell";

const useStyles = makeStyles(
  theme => {
    const dataDisplay: CSSProperties = {
      flex: "60%",
      overflow: "auto",
      textAlign: "left"
    };
    return {
      colAddress: {
        width: "auto"
      },
      colAging: {
        width: "100% !important"
      },
      colBin: {
        whiteSpace: "nowrap",
        width: "auto"
      },
      colBrands: {
        marginBottom: "2%"
      },
      colCustomer: {
        height: "auto",
        textAlign: "left",
        width: "auto",
        [theme.breakpoints.down("md")]: {
          paddingTop: "0px !important",
          width: "auto"
        }
      },
      colDelivered: {
        textAlign: "center",
        whiteSpace: "nowrap",
        width: "auto"
      },
      colDelivery: {
        whiteSpace: "nowrap",
        width: "auto"
      },
      colDisabled: {
        opacity: "0.5",
        pointerEvents: "none"
      },
      colDispatched: {
        textAlign: "center",
        whiteSpace: "nowrap",
        width: "auto"
      },
      colGrabStatus: {
        textAlign: "center",
        whiteSpace: "nowrap",
        width: "auto",
        [theme.breakpoints.down("md")]: {
          width: "auto"
        }
      },
      colLatLong: {
        whiteSpace: "nowrap",
        width: "auto"
      },
      colMall: {
        minWidth: "150px",
        width: "auto"
      },
      colNumber: {
        width: "auto",
        [theme.breakpoints.down("md")]: {
          "& thead": {
            display: "none !important"
          },
          "& thead, tbody, th, td, tr": {
            border: "none",
            display: "block"
          },
          "& tr": {
            backgroundColor: "#fff !important",
            borderBottom: "1px solid #ccc"
          },
          width: "auto"
        }
      },
      colOpsReason: {
        textAlign: "center",
        whiteSpace: "nowrap",
        width: "auto"
      },
      colOrderDate: {
        whiteSpace: "nowrap",
        width: "auto"
      },
      colPayment: {
        width: "auto",
        [theme.breakpoints.down("md")]: {
          width: "auto"
        }
      },
      colPrint: {
        width: "auto",
        [theme.breakpoints.down("md")]: {
          width: "auto"
        }
      },
      colReason: {
        textAlign: "center",
        width: "auto"
      },
      colRemarks: {
        height: "auto",
        textAlign: "left",
        width: "auto",
        [theme.breakpoints.down("md")]: {
          paddingTop: "0px !important",
          width: "auto"
        }
      },
      colRider: {
        whiteSpace: "nowrap",
        [theme.breakpoints.down("md")]: {
          width: "auto"
        },
        width: "auto"
      },
      colStatus: {
        width: "auto",
        [theme.breakpoints.down("md")]: {
          width: "auto"
        }
      },
      dataDisplayFlex: {
        display: "flex"
      },
      dataTitle: {
        display: "none !important"
      },
      deliveryType: {
        marginBottom: "6px",
        marginTop: "4px"
      },
      disabledPrint: {
        color: "#9b9b9b"
      },
      dropdownStyle: {
        backgroundColor: "lightgrey",
        border: "1px solid black",
        borderRadius: "5%"
      },
      ellipsis: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap"
      },
      link: {
        cursor: "pointer"
      },
      printIcon: {
        "&:hover": {
          color: "cornflowerblue"
        }
      },
      riderCellContent: {
        display: "flex",
        flex: "none",
        flexDirection: "column",
        fontSize: "11px",
        width: "10em"
      },
      riderCellContentText: {
        display: "flex",
        flex: "none",
        flexDirection: "column",
        width: "10em"
      },
      tableCellBody: {
        [theme.breakpoints.down("lg")]: {
          fontSize: "14px"
        }
      },
      tableCellHeader: {
        [theme.breakpoints.down("lg")]: {
          fontSize: "1em"
        }
      },
      [theme.breakpoints.down("md")]: {
        dataDisplay,
        dataGroup: {
          display: "flex"
        },
        dataTitle: {
          display: "block !important",
          flex: "40%",
          fontWeight: "bold",
          textAlign: "left"
        }
      },
      tableRoot: {
        "@media (min-width: 1440px)": {
          height: "35vh"
        },
        "@media (min-width: 926px)": {
          height: "65vh",
          overflowX: "auto"
        },
        [theme.breakpoints.down("md")]: {
          "& thead": {
            display: "none !important"
          },
          "& thead, tbody, th, td, tr": {
            border: "none",
            display: "block"
          },
          "& tr": {
            backgroundColor: "#fff !important",
            borderBottom: "1px solid #ccc",
            paddingBottom: "30px",
            paddingTop: "30px"
          }
        },
        height: "685px",
        overflowX: "auto",
        tableLayout: "auto"
      },
      tooltipHolder: {
        [theme.breakpoints.down("md")]: {
          pointerEvents: "none"
        }
      }
    };
  },
  { name: "OrderList" }
);

interface OrderListProps extends ListProps, SortPage<OrderListUrlSortField> {
  orders: MotherOrderList_orders_edges_node[];
  generateShippingDialog: (orderId: string) => void;
  onAssignRider: (orderId: string, orderCode: string, mallId: string) => void;
  onAddRemarks: (
    orderId: string,
    orderCode: string,
    orderOldRemarks?: string,
    orderOldRemarkId?: string
  ) => void;
  onOverrideStatus: (
    orderId: string,
    orderStatus: SmopOrderStatusEnum,
    newOrderStatus: SmopOrderStatusEnum,
    courier?: string
  ) => void;
  onUpdateStatus: (
    orderId: string,
    orderStatus: SmopOrderStatusEnum,
    newOrderStatus: SmopOrderStatusEnum,
    courier?: string
  ) => void;
  onCancelStatus: (courier: string, orderId: string) => void;
  onGrabStatusRefresh?: (orderId: string) => void;
  onPandaGoStatusRefresh?: (orderId: string) => void;
  refetchOrders;
  includedColumns: string[];
  disableAction: boolean;
  detailsPath(id: string): string;
}

export const OrderList: React.FC<OrderListProps> = props => {
  const {
    disabled,
    settings,
    orders,
    pageInfo,
    onPreviousPage,
    onNextPage,
    onUpdateListSettings,
    onSort,
    sort,
    generateShippingDialog,
    onAssignRider,
    onAddRemarks,
    onOverrideStatus,
    onUpdateStatus,
    onGrabStatusRefresh,
    onPandaGoStatusRefresh,
    onCancelStatus,
    refetchOrders,
    includedColumns,
    disableAction,
    detailsPath
  } = props;

  const classes = useStyles(props);
  const numberOfColumns = includedColumns.length;

  const [openAddressToolTip, setOpenAddressToolTip] = React.useState(false);

  const formatter = new Intl.NumberFormat("en-PH", {
    currency: "PHP",
    currencyDisplay: "symbol",
    minimumFractionDigits: 2,
    style: "currency"
  });

  const redirectOrder = id => {
    window.open(detailsPath(id));
  };

  const pathString = window.location.href;
  const mySubString = pathString
    .split("status=")
    .pop()
    .split("&")[0];

  let statusString = "";
  switch (mySubString) {
    case "NEW_ORDER":
      statusString = "New";
      break;
    case "ACKNOWLEDGED":
      statusString = "Acknowledged";
      break;
    case "READY":
      statusString = "Ready";
      break;
    case "COLLECTED":
      statusString = "Collected";
      break;
    case "RECEIVED_AT_FC":
      statusString = "Received at FC";
      break;
    case "PACKED":
      statusString = "Packed";
      break;
    case "READY_TO_SHIP":
      statusString = "Ready to ship";
      break;
    case "SHIPPED":
      statusString = "Shipped";
      break;
    case "RETURN_TO_FC":
      statusString = "Returned to FC";
      break;
    case "COMPLETED":
      statusString = "Completed";
      break;
    case "CANCELED":
      statusString = "Cancelled";
      break;
    default:
      break;
  }

  return (
    <div id="cmpAdminFulfillmentMotherOrderTab" className={classes.tableRoot}>
      <Table id="tblAdminFulfillmentMotherOrderTab">
        <TableHead id="tblHeadAdminFulfillmentMotherOrderTab">
          <TableRow>
            {includedColumns.includes("Order Status") && (
              <TableCellHeader
                id="tblCellHeaderAdminFulfillmentMotherOrderOrderStatus"
                className={`${classes.tableCellHeader} ${classes.colStatus}`}
              >
                <FormattedMessage
                  id="txtAdminFulfillmentMotherOrderOrderStatus"
                  defaultMessage="Order Status"
                  description="Order Status"
                />
              </TableCellHeader>
            )}
            {includedColumns.includes("Brands") && (
              <TableCellHeader
                id="tblCellHeaderAdminFulfillmentMotherOrderBrands"
                className={`${classes.tableCellHeader} ${classes.colBrands}`}
              >
                <FormattedMessage
                  id="txtAdminFulfillmentMotherOrderBrands"
                  defaultMessage="Brands"
                  description="Brands"
                />
              </TableCellHeader>
            )}
            {includedColumns.includes("Bin") && (
              <TableCellHeader
                id="tblCellHeaderAdminFulfillmentMotherOrderBin"
                className={`${classes.tableCellHeader} ${classes.colBin}`}
              >
                <FormattedMessage
                  id="txtCellHeaderAdminFulfillmentMotherOrderBin"
                  defaultMessage="Bin"
                  description="Bin"
                />
              </TableCellHeader>
            )}
            {includedColumns.includes("Order Date") && (
              <TableCellHeader
                id="tblCellHeaderAdminFulfillmentMotherOrderOrderDate"
                direction={
                  sort.sort === OrderListUrlSortField.date
                    ? getArrowDirection(sort.asc)
                    : undefined
                }
                onClick={() => onSort(OrderListUrlSortField.date)}
                className={`${classes.tableCellHeader} ${classes.colOrderDate}`}
              >
                <FormattedMessage
                  id="txtAdminFulfillmentMotherOrderDate"
                  defaultMessage="Order Date"
                  description="Order Date"
                />
              </TableCellHeader>
            )}
            {includedColumns.includes("Mother Order") && (
              <TableCellHeader
                id="tblCellHeaderAdminFulfillmentMotherOrderMotherOrder"
                className={`${classes.tableCellHeader} ${classes.colNumber}`}
              >
                <FormattedMessage
                  id="txtAdminFulfillmentMotherOrderMotherOrder"
                  defaultMessage="Mother Order"
                  description="Mother Order"
                />
              </TableCellHeader>
            )}
            {includedColumns.includes("Delivery") && (
              <TableCellHeader
                id="tblCellHeaderAdminFulfillmentMotherOrderDelivery"
                className={`${classes.tableCellHeader} ${classes.colDelivery}`}
              >
                <FormattedMessage
                  id="txtAdminFulfillmentMotherOrderMotherOrderDelivery"
                  defaultMessage="Delivery"
                  description="Delivery"
                />
              </TableCellHeader>
            )}
            {includedColumns.includes("3PL Status") && (
              <TableCellHeader
                id="tblCellHeaderAdminFulfillmentMotherOrder3PLStatus"
                className={`${classes.tableCellHeader} ${classes.colGrabStatus}`}
              >
                <FormattedMessage
                  id="txtAdminFulfillmentMotherOrder3PLStatus"
                  defaultMessage="3PL Status"
                  description="3PL Status"
                />
              </TableCellHeader>
            )}
            {includedColumns.includes("Aging") && (
              <TableCellHeader
                id="tblCellHeaderAdminFulfillmentMotherOrderAging"
                className={`${classes.tableCellHeader} ${classes.colAging}`}
              >
                <FormattedMessage
                  id="txtAdminFulfillmentMotherOrderAging"
                  defaultMessage="Aging"
                  description="Aging"
                />
              </TableCellHeader>
            )}
            {includedColumns.includes("Mall") && (
              <TableCellHeader
                id="tblCellHeaderAdminFulfillmentMotherOrderMall"
                className={`${classes.tableCellHeader} ${classes.colMall}`}
              >
                <FormattedMessage
                  id="txtAdminFulfillmentMotherOrderMall"
                  defaultMessage="Mall"
                  description="Mall"
                />
              </TableCellHeader>
            )}
            {includedColumns.includes("Payment") && (
              <TableCellHeader
                id="tblCellHeaderAdminFulfillmentMotherOrderPayment"
                className={`${classes.tableCellHeader} ${classes.colPayment}`}
              >
                <FormattedMessage
                  id="txtAdminFulfillmentMotherOrderPayment"
                  defaultMessage="Payment"
                  description="Payment"
                />
              </TableCellHeader>
            )}
            {includedColumns.includes("Customer") && (
              <TableCellHeader
                id="tblCellHeaderAdminFulfillmentMotherOrderCustomer"
                className={`${classes.tableCellHeader} ${classes.colCustomer}`}
              >
                <FormattedMessage
                  id="txtAdminFulfillmentMotherOrderCustomer"
                  defaultMessage="Customer"
                  description="Customer"
                />
              </TableCellHeader>
            )}
            {includedColumns.includes("Lat, Long") && (
              <TableCellHeader
                id="tblCellHeaderAdminFulfillmentMotherOrderLatLon"
                className={`${classes.tableCellHeader} ${classes.colLatLong}`}
              >
                <FormattedMessage
                  id="txtAdminFulfillmentMotherOrderLatLon"
                  defaultMessage="Lat, Long"
                  description="Lat, Long"
                />
              </TableCellHeader>
            )}
            {includedColumns.includes("Rider Name") && (
              <TableCellHeader
                id="tblCellHeaderAdminFulfillmentMotherOrderRiderName"
                className={`${classes.tableCellHeader} ${classes.colRider}`}
              >
                <FormattedMessage
                  id="txtAdminFulfillmentMotherOrderRiderName"
                  defaultMessage="Rider Name"
                  description="Rider Name"
                />
              </TableCellHeader>
            )}
            {includedColumns.includes("Time Dispatched") && (
              <TableCellHeader
                id="tblCellHeaderAdminFulfillmentMotherOrderTimeDispatched"
                className={`${classes.tableCellHeader} ${classes.colDispatched}`}
              >
                <FormattedMessage
                  id="txtAdminFulfillmentMotherOrderTimeDispatched"
                  defaultMessage="Time Dispatched"
                  description="Time Dispatched"
                />
              </TableCellHeader>
            )}
            {includedColumns.includes("Time Delivered") && (
              <TableCellHeader
                id="tblCellHeaderAdminFulfillmentMotherOrderTimeDelivered"
                className={`${classes.tableCellHeader} ${classes.colDelivered}`}
              >
                <FormattedMessage
                  id="txtAdminFulfillmentMotherOrderTimeDelivered"
                  defaultMessage="Time Delivered"
                  description="Time Delivered"
                />
              </TableCellHeader>
            )}
            {includedColumns.includes("On Time") && (
              <TableCellHeader
                id="tblCellHeaderAdminFulfillmentMotherOrderOnTime"
                className={classes.tableCellHeader}
                style={{ whiteSpace: "nowrap" }}
              >
                <FormattedMessage
                  id="txtAdminFulfillmentMotherOrderOnTime"
                  defaultMessage="On Time"
                  description="On Time"
                />
              </TableCellHeader>
            )}
            {includedColumns.includes("Reason") && (
              <TableCellHeader
                id="tblCellHeaderAdminFulfillmentMotherOrderReason"
                textAlign="center"
                className={`${classes.tableCellHeader} ${classes.colReason}`}
              >
                <FormattedMessage
                  id="txtAdminFulfillmentMotherOrderReason"
                  defaultMessage="Reason"
                  description="Reason"
                />
              </TableCellHeader>
            )}
            {includedColumns.includes("Ops Admin Reason for Late Delivery") && (
              <TableCellHeader
                id="tblCellHeaderAdminFulfillmentMotherOrderOpsAdminReasonLateDelivery"
                textAlign="center"
                className={`${classes.tableCellHeader} ${classes.colOpsReason}`}
              >
                <FormattedMessage
                  id="txtAdminFulfillmentMotherOrderOpsAdminReasonLateDelivery"
                  defaultMessage="Ops Admin Reason for Late Delivery"
                  description="Ops Admin Reason for Late Delivery"
                />
              </TableCellHeader>
            )}
            {includedColumns.includes("Remarks") && (
              <TableCellHeader
                id="tblCellHeaderAdminFulfillmentMotherOrderRemarks"
                className={`${classes.tableCellHeader} ${classes.colRemarks}`}
              >
                <FormattedMessage
                  id="txtAdminFulfillmentMotherOrderRemarks"
                  defaultMessage="Remarks"
                  description="Remarks"
                />
              </TableCellHeader>
            )}
            {includedColumns.includes("Print") && (
              <TableCellHeader
                id="tblCellHeaderAdminFulfillmentMotherOrderPrint"
                textAlign="center"
                className={`${classes.tableCellHeader} ${classes.colPrint}`}
              >
                {" "}
              </TableCellHeader>
            )}
          </TableRow>
        </TableHead>
        <TableFooter id="tblFooterAdminFulfillmentMotherOrder">
          <TableRow id="tblFooterRowAdminFulfillmentMotherOrderTablePagination">
            <TablePagination
              colSpan={numberOfColumns}
              settings={settings}
              hasNextPage={pageInfo && !disabled ? pageInfo.hasNextPage : false}
              onNextPage={onNextPage}
              onUpdateListSettings={onUpdateListSettings}
              hasPreviousPage={
                pageInfo && !disabled ? pageInfo.hasPreviousPage : false
              }
              onPreviousPage={onPreviousPage}
            />
          </TableRow>
        </TableFooter>
        <TableBody id="tblBodyAdminFulfillmentMotherOrder">
          {renderCollection(
            orders,
            (order, index) => (
              <TableRow
                hover={!!order}
                className={order ? classes.link : undefined}
                onClick={() => redirectOrder(order.id)}
                key={order ? index : "skeleton"}
              >
                {includedColumns.includes("Order Status") && (
                  <TableCell
                    className={`${classes.tableCellBody} ${classes.colStatus}`}
                    id={`tblCellAdminFulfillmentMotherOrderOrderStatus-${order?.orderCode}`}
                    onClick={e => {
                      e.stopPropagation();
                    }}
                  >
                    <div className={classes.dataGroup}>
                      <div className={classes.dataTitle}>Order Status</div>
                      <div
                        className={`${classes.dataDisplay} ${classes.dataDisplayFlex}`}
                      >
                        <RemittanceManualAdjustmentFlag
                          isRemittanceOrder={false}
                          disableAction={disableAction}
                          order={order}
                          onRefetchOrders={refetchOrders}
                        />
                        {maybe(() => order.smopStatus) ? (
                          (order.smopStatus !== SmopOrderStatusEnum.COMPLETED &&
                            order.smopStatus !== SmopOrderStatusEnum.CANCELED &&
                            (order?.shippingMethod?.deliveryType ===
                              DeliveryTypeEnum.EXPRESS ||
                              order?.shippingMethod?.deliveryType ===
                                DeliveryTypeEnum.EXPRESS_LONG_DISTANCE)) ||
                          order?.smopStatus === SmopOrderStatusEnum.SHIPPED ||
                          (order?.smopStatus ===
                            SmopOrderStatusEnum.RETURN_TO_FC &&
                            order?.verticalType ===
                              OrderVerticalType.GENERAL_MERCHANDISE) ||
                          order?.shippingMethod?.deliveryType ===
                            DeliveryTypeEnum.EXPRESS ||
                          order?.shippingMethod?.deliveryType ===
                            DeliveryTypeEnum.EXPRESS_LONG_DISTANCE ? (
                            <DropdownStatus
                              onCancelStatus={onCancelStatus}
                              onOverrideStatus={onOverrideStatus}
                              onUpdateStatus={onUpdateStatus}
                              order={order}
                              disabled={disableAction}
                            />
                          ) : (
                            <OrderStatus
                              status={order.smopStatus}
                              onClick={() => redirectOrder(order.id)}
                            />
                          )
                        ) : (
                          <Skeleton />
                        )}
                      </div>
                    </div>
                  </TableCell>
                )}
                {includedColumns.includes("Brands") && (
                  <TableCell
                    id={`tblCellAdminFulfillmentMotherOrderBrands-${order?.orderCode}`}
                    className={`${classes.tableCellBody} ${classes.colBrands}`}
                  >
                    <div className={classes.dataGroup}>
                      <div className={classes.dataTitle}>Brands</div>
                      <div className={classes.dataDisplay}>
                        <BrandCell subOrders={maybe(() => order?.subOrders)} />
                      </div>
                    </div>
                  </TableCell>
                )}
                {includedColumns.includes("Bin") && (
                  <TableCell
                    id={`tblCellAdminFulfillmentMotherOrderBin${order?.orderCode}`}
                    className={`${classes.tableCellBody} ${classes.colBin}`}
                  >
                    <div className={classes.dataGroup}>
                      <div className={classes.dataTitle}>Bin</div>
                      <div className={classes.dataDisplay}>
                        {maybe(() => order?.binCode) ? (
                          <>{order?.binCode}</>
                        ) : (
                          <>{"--"}</>
                        )}
                      </div>
                    </div>
                  </TableCell>
                )}
                {includedColumns.includes("Order Date") && (
                  <TableCell
                    id={`tblCellAdminFulfillmentMotherOrderOrderDate-${order?.orderCode}`}
                    className={`${classes.tableCellBody} ${classes.colOrderDate}`}
                  >
                    <div className={classes.dataGroup}>
                      <div className={classes.dataTitle}>Order Date</div>
                      <div className={classes.dataDisplay}>
                        {maybe(() => order.created) ? (
                          <>
                            <span>{moment(order.created).format("ll")}</span>{" "}
                            <br />
                            <span>{moment(order.created).format("LT")}</span>
                          </>
                        ) : (
                          <Skeleton />
                        )}
                      </div>
                    </div>
                  </TableCell>
                )}
                {includedColumns.includes("Mother Order") && (
                  <TableCell
                    id={`tblCellAdminFulfillmentMotherOrderMotherOrder-${order?.orderCode}`}
                    className={`${classes.tableCellBody} ${classes.colNumber}`}
                  >
                    <div className={classes.dataGroup}>
                      <div className={classes.dataTitle}>Mother Order</div>
                      <Tooltip
                        className={classes.tooltipHolder}
                        title={"#" + maybe(() => order.orderCode)}
                      >
                        <div
                          className={classNames(
                            classes.dataDisplay,
                            classes.ellipsis
                          )}
                        >
                          {maybe(() => order.orderCode && order.subOrders) ? (
                            <>
                              <span>{order.orderCode}</span>
                              <br />
                              <span>
                                <b>{order.subOrders.length}</b> Seller(s)
                              </span>
                            </>
                          ) : (
                            <Skeleton />
                          )}
                        </div>
                      </Tooltip>
                    </div>
                  </TableCell>
                )}
                {includedColumns.includes("Delivery") && (
                  <TableCell
                    id={`tblCellAdminFulfillmentMotherOrderDelivery-${order?.orderCode}`}
                    className={`${classes.tableCellBody} ${classes.colDelivery}`}
                  >
                    <div className={classes.dataGroup}>
                      <div className={classes.dataTitle}>Delivery</div>
                      <div className={classes.dataDisplay}>
                        {maybe(() => order.shippingMethod) ? (
                          <div className={classes.deliveryType}>
                            {" "}
                            <OrderDeliveryType
                              delType={maybe(
                                () => order.shippingMethod.deliveryType,
                                null
                              )}
                            />
                          </div>
                        ) : (
                          <Skeleton />
                        )}
                        {maybe(() => order.shippingSchedule) ? (
                          <span>
                            {moment(order.shippingSchedule).format("ll")}
                          </span>
                        ) : (
                          <span>-</span>
                        )}
                      </div>
                    </div>
                  </TableCell>
                )}
                {includedColumns.includes("3PL Status") && (
                  <TableCell
                    id={`tblCellAdminFulfillmentMotherOrder3PLStatus-${order?.orderCode}`}
                    className={`${classes.tableCellBody} ${classes.colGrabStatus}`}
                    onClick={e => e.stopPropagation()}
                  >
                    <div className={classes.dataGroup}>
                      <div className={classes.dataTitle}>3PL Status</div>
                      <div
                        className={
                          disableAction
                            ? `${classes.dataDisplay} ${classes.colDisabled}`
                            : classes.dataDisplay
                        }
                      >
                        {maybe(() => order.courier) && (
                          <CourierName courierName={order.courier} />
                        )}
                        <ThreePLStatusCell
                          order={order}
                          onGrabStatusRefresh={onGrabStatusRefresh}
                          onPandaGoStatusRefresh={onPandaGoStatusRefresh}
                          onRefreshList={refetchOrders}
                        />
                      </div>
                    </div>
                  </TableCell>
                )}
                {includedColumns.includes("Aging") && (
                  <TableCell
                    id={`tblCellAdminFulfillmentMotherOrderAging-${order?.orderCode}`}
                    className={`${classes.tableCellBody} ${classes.colAging}`}
                  >
                    <div className={classes.dataGroup}>
                      <div className={classes.dataTitle}>Aging</div>
                      <div className={classes.dataDisplay}>
                        {maybe(() => order.aging) ? (
                          <AgingCell>{order.aging}</AgingCell>
                        ) : (
                          <Skeleton />
                        )}
                      </div>
                    </div>
                  </TableCell>
                )}
                {includedColumns.includes("Mall") && (
                  <TableCell
                    id={`tblCellAdminFulfillmentMotherOrderMall-${order?.orderCode}`}
                    className={`${classes.tableCellBody} ${classes.colMall}`}
                  >
                    <div className={classes.dataGroup}>
                      <div className={classes.dataTitle}>Mall</div>
                      <div className={classes.dataDisplay}>
                        {maybe(() => order.mall.name) ? (
                          <span>{order.mall.name}</span>
                        ) : (
                          <Skeleton />
                        )}
                      </div>
                    </div>
                  </TableCell>
                )}
                {includedColumns.includes("Payment") && (
                  <TableCell
                    id={`tblCellAdminFulfillmentMotherOrderPayment-${order?.orderCode}`}
                    className={`${classes.tableCellBody} ${classes.colPayment}`}
                  >
                    <div className={classes.dataGroup}>
                      <div className={classes.dataTitle}>Payment</div>
                      <div className={classes.dataDisplay}>
                        {maybe(() => order.payments[0].paymentMethodType) ? (
                          <>
                            <span>{getPaymentChannelCode(order)}</span>
                            <br />
                            <span>
                              {formatter.format(order.total.gross.amount)}
                            </span>
                          </>
                        ) : (
                          <Skeleton />
                        )}
                      </div>
                    </div>
                  </TableCell>
                )}
                {includedColumns.includes("Customer") && (
                  <TableCell
                    id={`tblCellAdminFulfillmentMotherOrderCustomer-${order?.orderCode}`}
                    className={`${classes.tableCellBody} ${classes.colCustomer}`}
                    onClick={e =>
                      openAddressToolTip ? e.stopPropagation() : null
                    }
                  >
                    <div className={classes.dataGroup}>
                      <div className={classes.dataTitle}>Customer</div>
                      <div className={classes.dataDisplay}>
                        {maybe(() => order.user) ? (
                          <CustomerCell
                            order={order}
                            setOpenAddressToolTip={setOpenAddressToolTip}
                          />
                        ) : (
                          <Skeleton />
                        )}
                      </div>
                    </div>
                  </TableCell>
                )}
                {includedColumns.includes("Lat, Long") && (
                  <TableCell
                    id={`tblCellAdminFulfillmentMotherOrderLatLon-${order?.orderCode}`}
                    className={`${classes.tableCellBody} ${classes.colLatLong}`}
                  >
                    <div className={classes.dataGroup}>
                      <div className={classes.dataTitle}>Lat, Long</div>
                      <div className={classes.dataDisplay}>
                        {maybe(() => order.user) ? (
                          <span>
                            {order.shippingAddress.latitude},{" "}
                            {order.shippingAddress.longitude}
                          </span>
                        ) : (
                          <Skeleton />
                        )}
                      </div>
                    </div>
                  </TableCell>
                )}
                {includedColumns.includes("Rider Name") && (
                  <TableCell
                    id={`tblCellAdminFulfillmentMotherOrderRiderName-${order?.orderCode}`}
                    className={`${classes.tableCellBody} ${classes.colRider}`}
                    onClick={e => e.stopPropagation()}
                  >
                    <div className={classes.dataGroup}>
                      <div className={classes.dataTitle}>Rider Name</div>
                      <div
                        className={
                          disableAction
                            ? `${classes.dataDisplay} ${classes.colDisabled}`
                            : classes.dataDisplay
                        }
                      >
                        <RiderCell
                          order={order}
                          onAssignRider={onAssignRider}
                        />
                      </div>
                    </div>
                  </TableCell>
                )}
                {includedColumns.includes("Time Dispatched") && (
                  <TableCell
                    id={`tblCellAdminFulfillmentMotherOrderTimeDispatched-${order?.orderCode}`}
                    className={`${classes.tableCellBody} ${classes.colDispatched}`}
                  >
                    <div className={classes.dataGroup}>
                      <div className={classes.dataTitle}>Time Dispatched</div>
                      <div className={classes.dataDisplay}>
                        <span>
                          {maybe(() => order?.shippedAt)
                            ? moment(order.shippedAt).format("hh:mm A")
                            : "--"}
                        </span>
                      </div>
                    </div>
                  </TableCell>
                )}
                {includedColumns.includes("Time Delivered") && (
                  <TableCell
                    id={`tblCellAdminFulfillmentMotherOrderTimeDelivered-${order?.orderCode}`}
                    className={`${classes.tableCellBody} ${classes.colDelivered}`}
                  >
                    <div className={classes.dataGroup}>
                      <div className={classes.dataTitle}>Time Delivered</div>
                      <div className={classes.dataDisplay}>
                        <span>
                          {" "}
                          {maybe(() => order?.completedAt)
                            ? moment(order?.completedAt).format("hh:mm A")
                            : "--"}
                        </span>
                      </div>
                    </div>
                  </TableCell>
                )}
                {includedColumns.includes("On Time") && (
                  <TableCell
                    id={`tblCellAdminFulfillmentMotherOrderOnTime-${order?.orderCode}`}
                    className={classes.tableCellBody}
                    style={{ textAlign: "center", whiteSpace: "nowrap" }}
                  >
                    <div className={classes.dataGroup}>
                      <div className={classes.dataTitle}>On Time</div>
                      <div className={classes.dataDisplay}>
                        <span>
                          {order?.onTime !== null
                            ? onTimeIsTrue(order?.onTime)
                            : "--"}
                        </span>
                      </div>
                    </div>
                  </TableCell>
                )}
                {includedColumns.includes("Reason") && (
                  <TableCell
                    id={`tblCellAdminFulfillmentMotherOrderReason-${order?.orderCode}`}
                    className={classes.colReason}
                  >
                    <div className={classes.dataGroup}>
                      <div className={classes.dataTitle}>Reason</div>
                      <div className={classes.dataDisplay}>
                        {maybe(() => order) ? (
                          <>
                            {order.cancelReason ? (
                              <CancelReasonField
                                cancelReason={order.cancelReason}
                              />
                            ) : (
                              "N/A"
                            )}
                          </>
                        ) : (
                          <Skeleton />
                        )}
                      </div>
                    </div>
                  </TableCell>
                )}
                {includedColumns.includes(
                  "Ops Admin Reason for Late Delivery"
                ) && (
                  <TableCell
                    id={`tblCellAdminFulfillmentMotherOrderOpsAdminReasonLateDelivery-${order?.orderCode}`}
                    className={classes.colOpsReason}
                  >
                    <div className={classes.dataGroup}>
                      <div className={classes.dataTitle}>
                        Ops Admin Reason for Late Delivery
                      </div>
                      <div
                        className={
                          disableAction
                            ? `${classes.dataDisplay} ${classes.colDisabled}`
                            : classes.dataDisplay
                        }
                      >
                        <OpsAdminReasonForLate
                          order={order}
                          refetchOrders={refetchOrders}
                        />
                      </div>
                    </div>
                  </TableCell>
                )}
                {includedColumns.includes("Remarks") && (
                  <TableCell
                    id={`tblCellAdminFulfillmentMotherOrderRemarks-${order?.orderCode}`}
                    className={`${classes.tableCellBody} ${classes.colRemarks}`}
                    onClick={e => e.stopPropagation()}
                  >
                    <div className={classes.dataGroup}>
                      <div className={classes.dataTitle}>Remarks</div>
                      <div
                        className={
                          disableAction
                            ? `${classes.dataDisplay} ${classes.colDisabled}`
                            : classes.dataDisplay
                        }
                      >
                        {maybe(() => order) ? (
                          <RemarksCell
                            order={order}
                            onAddRemarks={onAddRemarks}
                          />
                        ) : (
                          <Skeleton />
                        )}
                      </div>
                    </div>
                  </TableCell>
                )}
                {includedColumns.includes("Print") && (
                  <TableCell
                    id={`tblCellAdminFulfillmentMotherOrderPrint-${order?.orderCode}`}
                    className={`${classes.tableCellBody} ${classes.colPrint}`}
                    onClick={e => e.stopPropagation()}
                  >
                    <div className={classes.dataGroup}>
                      <div className={classes.dataTitle}></div>
                      <div
                        className={
                          disableAction
                            ? `${classes.dataDisplay} ${classes.colDisabled}`
                            : classes.dataDisplay
                        }
                      >
                        {maybe(() => order) ? (
                          <PrintIcon
                            className={classNames(null, {
                              [classes.printIcon]:
                                order?.shippingMethod?.deliveryType ===
                                  DelEnum.EXPRESS ||
                                order?.shippingMethod?.deliveryType ===
                                  DelEnum.EXPRESS_LONG_DISTANCE ||
                                order?.shippingMethod?.deliveryType ===
                                  DelEnum.SAME_DAY,
                              [classes.disabledPrint]:
                                order?.shippingMethod?.deliveryType !==
                                  DelEnum.EXPRESS &&
                                order?.shippingMethod?.deliveryType !==
                                  DelEnum.EXPRESS_LONG_DISTANCE &&
                                order?.shippingMethod?.deliveryType !==
                                  DelEnum.SAME_DAY
                            })}
                            fontSize="large"
                            onClick={() =>
                              order.shippingMethod.deliveryType ===
                                DelEnum.EXPRESS ||
                              order?.shippingMethod?.deliveryType ===
                                DelEnum.EXPRESS_LONG_DISTANCE ||
                              order?.shippingMethod?.deliveryType ===
                                DelEnum.SAME_DAY
                                ? generateShippingDialog(order.id)
                                : null
                            }
                          />
                        ) : (
                          <Skeleton />
                        )}
                      </div>
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ),
            () => (
              <TableRow>
                <TableCell colSpan={numberOfColumns}>
                  {"No " + statusString + " orders found"}
                </TableCell>
              </TableRow>
            )
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default OrderList;
